/**
 * RightSidePanel — panel lateral derecho con tabs.
 *
 * Diseñado para vivir DENTRO del slot del ProjectShell (ver
 * `routes/projects/rightPanelSlot.tsx`). El componente llena su
 * contenedor (`h-full`); el shell le da el ancho fijo (400px) y la
 * posición vertical (entre el project header y el bottom del work
 * area). Sin `position: fixed`, sin CSS vars, sin DOM-walking — la
 * geometría es 100% flexbox.
 *
 * Múltiples tabs intercambiables en el header. Para Fase 5.d solo se
 * monta el tab 'comments'. En sprints futuros entrarán otros:
 *   - AI chat (preguntar dudas del proyecto)
 *   - Inconsistencies detector
 *   - Citations browser
 *
 * Patrón de uso:
 *
 * ```tsx
 * import { useRightPanel } from '../../routes/projects/rightPanelSlot'
 *
 * useRightPanel(
 *   panelOpen ? (
 *     <RightSidePanel
 *       onClose={() => setPanelOpen(false)}
 *       tabs={[
 *         { key: 'comments', label: t('comments_panel.tab'),
 *           content: <CommentsPanel ... /> },
 *       ]}
 *     />
 *   ) : null
 * )
 * ```
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'

import {
  RIGHT_PANEL_MAX_WIDTH,
  RIGHT_PANEL_MIN_WIDTH,
  useRightPanelLayoutStore,
} from '../stores/rightPanelLayoutStore'

export interface RightSidePanelTab {
  key: string
  label: string
  icon?: React.ReactNode
  /** Badge numérico opcional (ej. count de comments abiertos). */
  count?: number
  content: React.ReactNode
}

export interface RightSidePanelProps {
  onClose: () => void
  tabs: RightSidePanelTab[]
  /** Tab activo al abrir por primera vez. Default: el primero. */
  defaultTabKey?: string
  /** Modo controlado: si se pasa, el tab activo lo decide el padre. */
  activeTabKey?: string
  onTabChange?: (key: string) => void
  /** Aria label del aside (para accesibilidad). Default: "Panel lateral". */
  ariaLabel?: string
}

export function RightSidePanel({
  onClose,
  tabs,
  defaultTabKey,
  activeTabKey: controlledActive,
  onTabChange,
  ariaLabel = 'Right side panel',
}: RightSidePanelProps) {
  const firstTabKey = tabs[0]?.key
  const [internalActive, setInternalActive] = useState<string>(
    defaultTabKey ?? firstTabKey ?? '',
  )
  const activeKey = controlledActive ?? internalActive

  // ESC cierra (escape como salida natural). El componente solo se
  // monta cuando el padre quiere mostrarlo, así que el listener vive
  // mientras el panel esté visible.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  if (tabs.length === 0) return null

  const activeTab =
    tabs.find((t) => t.key === activeKey) ?? tabs[0]
  if (!activeTab) return null

  function handleTabClick(key: string) {
    if (controlledActive === undefined) setInternalActive(key)
    onTabChange?.(key)
  }

  return (
    // Llena el contenedor que el shell le dio (`h-full` por el slot).
    // `relative` para anclar el ResizeHandle absoluto en el borde
    // izquierdo. Flex column: header (tabs + close) shrink-0, body
    // flex-1 con scroll interno gestionado por cada tab.
    <div
      role="complementary"
      aria-label={ariaLabel}
      className="h-full flex flex-col min-h-0 relative"
    >
      {/* Doc 21 Fase 10 — handle absolute en el borde izquierdo del
          panel. Vive aquí (no en el shell) porque solo aplica cuando
          el panel está montado; cuando el aside está vacío el
          `empty:hidden` ya lo oculta junto con el panel. */}
      <ResizeHandle />
      {/* Header con tabs + close */}
      <header className="flex items-stretch border-b border-line shrink-0">
        <nav
          className="flex flex-1 min-w-0 overflow-x-auto"
          role="tablist"
          aria-label={ariaLabel}
        >
          {tabs.map((tab) => {
            const isActive = tab.key === activeKey
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`right-panel-tab-${tab.key}`}
                onClick={() => handleTabClick(tab.key)}
                className={
                  isActive
                    ? 'inline-flex items-center gap-1.5 px-4 py-3 text-xs font-medium text-brand border-b-2 border-brand bg-surface'
                    : 'inline-flex items-center gap-1.5 px-4 py-3 text-xs font-medium text-ink-muted border-b-2 border-transparent hover:text-ink hover:bg-surface-muted'
                }
              >
                {tab.icon}
                <span>{tab.label}</span>
                {typeof tab.count === 'number' && tab.count > 0 ? (
                  <span
                    className={
                      isActive
                        ? 'px-1.5 py-0.5 rounded-full bg-brand-soft text-brand text-[10px] font-mono'
                        : 'px-1.5 py-0.5 rounded-full bg-surface-muted text-ink-muted text-[10px] font-mono'
                    }
                  >
                    {tab.count}
                  </span>
                ) : null}
              </button>
            )
          })}
        </nav>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 px-3 text-ink-subtle hover:text-ink hover:bg-surface-muted border-l border-line"
          aria-label="Close panel"
          title="Close panel"
        >
          <X className="w-4 h-4" />
        </button>
      </header>

      {/* Active tab content. Cada tab es responsable de su propio
          scroll interno (e.g., CommentsViewerPanel hace flex-col
          h-full con body scrollable + form abajo). */}
      <div
        id={`right-panel-tab-${activeTab.key}`}
        role="tabpanel"
        aria-labelledby={`right-panel-tab-${activeTab.key}-label`}
        className="flex-1 min-h-0 overflow-hidden"
      >
        {activeTab.content}
      </div>
    </div>
  )
}

// ─── ResizeHandle ──────────────────────────────────────────────────────────
/**
 * Divider draggable en el borde izquierdo del panel.
 *
 * Mouse:
 *   · mousedown → activa drag, pone cursor + bloquea selection en body
 *     mientras dura el gesto.
 *   · mousemove → nuevo width = `innerWidth − clientX` (el aside está
 *     pegado a la derecha). El store hace el clamp [320, 720].
 *   · mouseup → libera body styles.
 *
 * Dblclick → reset al default (400px).
 *
 * Teclado (a11y WCAG 2.1):
 *   · ←/→ ajustan ±10px.
 *   · Home/End van al min/max.
 *   · Shift+←/→ ajustan ±40px (paso grande).
 *
 * Render: un track translucido fijo de 4px de ancho, posicionado en
 * `-left-0.5` para que el área "agarrable" se sienta de 8px (overlap
 * con el borde del aside). Visualmente el track gana opacidad en hover
 * y se vuelve `bg-brand` durante el drag.
 */
function ResizeHandle() {
  const { t } = useTranslation('common')
  const width = useRightPanelLayoutStore((s) => s.width)
  const setWidth = useRightPanelLayoutStore((s) => s.setWidth)
  const resetWidth = useRightPanelLayoutStore((s) => s.resetWidth)
  const [dragging, setDragging] = useState(false)
  const handleRef = useRef<HTMLDivElement | null>(null)

  // Mousedown: arrancamos el drag. Adjuntamos listeners globales para
  // que el drag sobreviva cuando el cursor sale del propio handle.
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault() // evita iniciar selección de texto del DOM
      setDragging(true)
    },
    [],
  )

  // Effect que activa/desactiva los listeners globales durante el drag.
  // Bloquea cursor + selection del body para feedback claro y para que
  // el cursor `ew-resize` no parpadee al cruzar bordes de elementos.
  useEffect(() => {
    if (!dragging) return
    const onMove = (e: MouseEvent) => {
      const next = window.innerWidth - e.clientX
      setWidth(next)
    }
    const onUp = () => setDragging(false)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    const prevCursor = document.body.style.cursor
    const prevUserSelect = document.body.style.userSelect
    document.body.style.cursor = 'ew-resize'
    document.body.style.userSelect = 'none'
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      document.body.style.cursor = prevCursor
      document.body.style.userSelect = prevUserSelect
    }
  }, [dragging, setWidth])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const step = e.shiftKey ? 40 : 10
      // Convención WAI-ARIA: en un separator vertical entre paneles
      // izquierdo/derecho, ← aumenta el ancho del panel derecho (lo
      // empuja hacia la izquierda) y → lo reduce. Coincide con la
      // intuición del usuario sobre "qué dirección estoy arrastrando".
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        setWidth(width + step)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        setWidth(width - step)
      } else if (e.key === 'Home') {
        e.preventDefault()
        setWidth(RIGHT_PANEL_MAX_WIDTH)
      } else if (e.key === 'End') {
        e.preventDefault()
        setWidth(RIGHT_PANEL_MIN_WIDTH)
      }
    },
    [setWidth, width],
  )

  return (
    <div
      ref={handleRef}
      role="separator"
      aria-orientation="vertical"
      aria-label={t('right_panel.resize_handle_label')}
      aria-valuenow={width}
      aria-valuemin={RIGHT_PANEL_MIN_WIDTH}
      aria-valuemax={RIGHT_PANEL_MAX_WIDTH}
      tabIndex={0}
      onMouseDown={handleMouseDown}
      onDoubleClick={resetWidth}
      onKeyDown={handleKeyDown}
      title={t('right_panel.resize_handle_title')}
      className={
        dragging
          ? 'absolute -left-0.5 top-0 bottom-0 w-1 cursor-ew-resize bg-brand z-10 focus:outline-none'
          : 'absolute -left-0.5 top-0 bottom-0 w-1 cursor-ew-resize bg-transparent hover:bg-brand/40 transition-colors z-10 focus:outline-none focus:bg-brand/60'
      }
    />
  )
}
